import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../../components/NtText";
import NtMaterialIcon from "../../../components/NtMaterialIcon";
import NtTouchableEffect from "../../../components/NtTouchableEffect";
import { useTheme } from "../../../context/ThemeContext";

// When Plan + Payment come back into the active flow, restore the Plan and
// Payment <Section>s in this file (the originals lived here pre-restructure).

function Row({ label, value, leading }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  return (
    <View style={styles.row}>
      <NtText style={styles.rowLabel}>{label}</NtText>
      <View style={styles.rowValueWrap}>
        {leading}
        <NtText style={styles.rowValue}>{value || "—"}</NtText>
      </View>
    </View>
  );
}

function Section({ icon, title, onEdit, children }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  return (
    <View style={styles.section}>
      <View style={styles.sectionHead}>
        <View style={styles.sectionTitle}>
          <View style={styles.sectionIcon}>
            <NtMaterialIcon name={icon} size={16} color={theme.brand2} />
          </View>
          <NtText style={styles.sectionTitleText}>{title}</NtText>
        </View>
        <NtTouchableEffect onPress={onEdit} style={styles.editBtn}>
          <NtMaterialIcon name="pencil-outline" size={13} color={theme.brand2} />
          <NtText style={styles.editText}>Edit</NtText>
        </NtTouchableEffect>
      </View>
      <View style={styles.sectionBody}>{children}</View>
    </View>
  );
}

function NtOnboardingReviewStep({ company, admin, onEditStep }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <View>
      <View style={styles.banner}>
        <NtMaterialIcon name="clipboard-check-outline" size={18} color={theme.brand2} />
        <NtText style={styles.bannerText}>
          Almost there — review the details below. Hit{" "}
          <NtText style={styles.bannerStrong}>Continue</NtText> to verify your
          email and create the account.
        </NtText>
      </View>

      <Section
        icon="domain"
        title="Company"
        onEdit={() => onEditStep && onEditStep("company")}
      >
        <Row label="Company name" value={company?.name} />
        <Row label="Company alias" value={company?.domain_name} />
        <Row label="Description" value={company?.description} />
      </Section>

      <Section
        icon="account-outline"
        title="Admin user"
        onEdit={() => onEditStep && onEditStep("admin")}
      >
        <Row label="Full name" value={admin?.name} />
        <Row label="Email" value={admin?.email} />
        <Row label="Password" value={admin?.password ? "•••••••• (set)" : "—"} />
      </Section>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    banner: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      borderRadius: t.radius,
      paddingVertical: 14,
      paddingHorizontal: 16,
      marginBottom: 18,
      maxWidth: 880,
    },
    bannerText: { flex: 1, fontSize: 13, color: t.text, lineHeight: 18 },
    bannerStrong: { fontWeight: "700", color: t.text },

    section: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.border,
      borderRadius: t.radiusCard,
      marginBottom: 14,
      overflow: "hidden",
      maxWidth: 880,
    },
    sectionHead: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
    },
    sectionTitle: { flexDirection: "row", alignItems: "center", gap: 10 },
    sectionIcon: {
      width: 28,
      height: 28,
      borderRadius: 8,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      alignItems: "center",
      justifyContent: "center",
    },
    sectionTitleText: { fontSize: 14, fontWeight: "700", color: t.text },
    editBtn: { flexDirection: "row", alignItems: "center", gap: 4 },
    editText: { fontSize: 12, fontWeight: "600", color: t.brand2 },

    sectionBody: { paddingVertical: 4, paddingHorizontal: 16 },
    row: {
      flexDirection: "row",
      alignItems: "flex-start",
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
      gap: 14,
    },
    rowLabel: {
      width: 140,
      fontSize: 12,
      color: t.textDim,
      fontWeight: "500",
    },
    rowValueWrap: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    rowValue: { flex: 1, fontSize: 13, color: t.text, fontWeight: "500" },
  });

export default NtOnboardingReviewStep;
